export interface PositionInformation
    extends Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'> {
    isInViewport: boolean;
    isInViewportVertically: boolean;
    isInViewportHorizontally: boolean;
    viewportWidth: number;
    viewportHeight: number;
    viewportElement: Element;
    element: HTMLElement;

    /**
     * The distance of the elements lower end to the lower end of the viewport/parent
     */
    offsetBottom: number;

    /**
     * The distance of the elements right end to the right end of the viewport/parent
     */
    offsetRight: number;
}

/**
 * Get information about the given element's placement in the viewport or the relative to
 * the given parent element.
 * All measurements are rounded to integers and considered full pixels to correctly
 * apply to non-highres diplays
 */
export function getElementPositionInformation(
    element: HTMLElement,
    options: {
        /**
         * If true, the `isInViewport` return properties only signal a 'true' state, if the given
         * element is fully inside the viewport. Otherwise, partial visibility in the viewport is considered 'true'.
         */
        strict?: boolean;
        /**
         * If not set, matches on both axis
         */
        axis?: 'all' | 'vertical' | 'horizontal';
    } = {},
    /**
     * Set a different boundary box to measure from. The default is the `window`
     */
    viewportElement?: HTMLElement
): PositionInformation {
    const bounding = element.getBoundingClientRect();
    const elementHeight = element.offsetHeight;
    const elementWidth = element.offsetWidth;

    options.axis = options.axis || 'all';

    const checkVertically = ['all', 'vertical'].includes(options.axis);
    const checkHorizontally = ['all', 'horizontal'].includes(options.axis);

    let verticalMatch = !checkVertically;
    let horizontalMatch = !checkHorizontally;

    let viewportVerticalStart = 0;
    let viewportHorizontalStart = 0;
    let viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;
    let viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

    let top = Math.floor(bounding.top);
    let right = Math.floor(bounding.right);
    let left = Math.floor(bounding.left);
    let bottom = Math.floor(bounding.bottom);
    let offsetBottom = viewportHeight - bottom;
    let offsetRight = viewportWidth - right;

    if (viewportElement) {
        const parentBounding = viewportElement.getBoundingClientRect();
        viewportVerticalStart = Math.floor(parentBounding.top);
        viewportHorizontalStart = Math.floor(parentBounding.left);
        viewportWidth = Math.floor(parentBounding.width);
        viewportHeight = Math.floor(parentBounding.height);

        top = top - viewportVerticalStart;
        right = right - viewportHorizontalStart;
        left = left - viewportHorizontalStart;
        bottom = bottom - viewportVerticalStart;
        offsetBottom = Math.floor(parentBounding.bottom) - bottom;
        offsetRight = Math.floor(parentBounding.right) - right;
    }

    if (options.strict) {
        verticalMatch =
            verticalMatch ||
            (top >= viewportVerticalStart && bottom <= viewportHeight);

        horizontalMatch =
            horizontalMatch ||
            (left >= viewportHorizontalStart && right <= viewportWidth);
    } else {
        verticalMatch =
            verticalMatch ||
            (top >= -elementHeight && bottom <= viewportHeight + elementHeight);

        horizontalMatch =
            horizontalMatch ||
            (left >= -elementHeight && right <= viewportWidth + elementHeight);
    }

    const inViewport = verticalMatch && horizontalMatch;

    return {
        isInViewport: inViewport,
        isInViewportVertically: verticalMatch,
        isInViewportHorizontally: horizontalMatch,
        viewportWidth,
        viewportHeight,
        top,
        right,
        bottom,
        left,
        offsetRight,
        offsetBottom,
        viewportElement: viewportElement || document.documentElement,
        element,
    };
}
